import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { dcrResponseOptionLabel, dcrScreenLabels, dcrScreensFromFlow, fetchDcrBranchingFlow, fetchDcrParticipantByNumber, fetchDcrParticipantTaskResponses, fetchDcrParticipantVisitSchedule, fetchDcrSites, selectableDcrScreens, } from '../../utils';
const OPTIONS_QUERY_KEY = 'dataChangeRequestOptions';
/** Participant numbering is captured by an activity that must never be changed through a DCR */
const EXCLUDED_ACTIVITY_NAMES = ['enter participant number'];
const INACTIVE_STATUSES = ['inactive', 'deactivated'];
const CLINRO = 'clinro';
const visitIdOf = (taskResponse) => typeof taskResponse.c_visit === 'string' ? taskResponse.c_visit : (taskResponse.c_visit?._id ?? '');
const isSelectableResponse = (taskResponse) => {
    const activityName = (taskResponse.c_task?.c_name ?? '').trim().toLowerCase();
    return (!!taskResponse.c_task &&
        !EXCLUDED_ACTIVITY_NAMES.includes(activityName) &&
        !INACTIVE_STATUSES.includes((taskResponse.c_status ?? '').toLowerCase()));
};
export const useDcrParticipant = ({ apiInstance, environmentId }, siteId, participantNumber) => useQuery({
    queryKey: [OPTIONS_QUERY_KEY, 'participant', environmentId, siteId, participantNumber],
    queryFn: () => fetchDcrParticipantByNumber(apiInstance, siteId, participantNumber),
    enabled: !!siteId && !!participantNumber,
    retry: false,
});
export const useDcrSites = ({ apiInstance, environmentId }, enabled = true) => useQuery({
    queryKey: [OPTIONS_QUERY_KEY, 'sites', environmentId],
    queryFn: () => fetchDcrSites(apiInstance),
    enabled,
});
export const useDcrParticipantVisits = ({ apiInstance, environmentId }, siteId, participantId) => useQuery({
    queryKey: [OPTIONS_QUERY_KEY, 'visitSchedule', environmentId, siteId, participantId],
    queryFn: () => fetchDcrParticipantVisitSchedule(apiInstance, siteId, participantId),
    enabled: !!siteId && !!participantId,
});
export const useDcrParticipantResponses = ({ apiInstance, environmentId }, participantId) => useQuery({
    queryKey: [OPTIONS_QUERY_KEY, 'taskResponses', environmentId, participantId],
    queryFn: () => fetchDcrParticipantTaskResponses(apiInstance, participantId),
    enabled: !!participantId,
});
/**
 * The activity's screens and its branching rules. The screen list and the branching form both read
 * this one query, so picking a screen to change costs no further request.
 */
export const useDcrFlow = ({ apiInstance, environmentId }, taskResponseId, enabled = true) => useQuery({
    queryKey: [OPTIONS_QUERY_KEY, 'flow', environmentId, taskResponseId],
    queryFn: () => fetchDcrBranchingFlow(apiInstance, taskResponseId),
    enabled: enabled && !!taskResponseId,
});
/**
 * Resolves every option list a DCR type's fields need. Everything comes from three
 * participant-scoped queries (visit schedule, responses, screens) plus the site list, so a
 * cascade stays internally consistent without one request per dropdown.
 */
export const useDcrFieldOptions = ({ context, values, siteId, participantId, currentSiteNumber, participantTimeZone, sources, }) => {
    const needs = (source) => sources.includes(source);
    const needsVisits = needs('visits') || needs('desired_visits');
    const needsResponses = needs('participant_responses') ||
        needs('epro_responses') ||
        needs('participant_activities') ||
        needs('activities') ||
        needs('site_task_responses') ||
        needs('visit_activities');
    const sitesQuery = useDcrSites(context, needs('sites'));
    const visitsQuery = useDcrParticipantVisits(context, needsVisits ? siteId : undefined, participantId);
    const responsesQuery = useDcrParticipantResponses(context, needsResponses ? participantId : undefined);
    const selectedTaskResponseId = String(values.dcr_intake__task_response_id ?? '');
    const flowQuery = useDcrFlow(context, needs('screens') ? selectedTaskResponseId || undefined : undefined);
    const scheduledVisits = visitsQuery.data ?? [];
    const responses = useMemo(() => (responsesQuery.data ?? []).filter(isSelectableResponse), [responsesQuery.data]);
    const screens = useMemo(() => dcrScreensFromFlow(flowQuery.data), [flowQuery.data]);
    const screenLabels = useMemo(() => dcrScreenLabels(screens), [screens]);
    const selectedScreen = screens.find((screen) => String(screen.stepResponseId) === String(values.dcr_intake__step_response_id ?? ''));
    const options = useMemo(() => {
        const selectedVisitId = String(values.dcr_intake__visit_id ?? '');
        const selectedActivityId = String(values.dcr_intake__activity_id ?? '');
        const visitOptions = scheduledVisits.map((visit) => ({
            label: visit.c_name ?? String(visit.c_visit),
            value: String(visit.c_visit),
            meta: { visitDate: visit.c_window_start_date },
        }));
        const responsesForVisit = responses.filter((response) => visitIdOf(response) === selectedVisitId);
        const siteResponses = responses.filter((response) => response.c_task?.c_observation_type === CLINRO);
        const participantResponses = responses.filter((response) => response.c_task?.c_observation_type !== CLINRO);
        const uniqueActivities = (list) => {
            const seen = new Map();
            for (const response of list) {
                const activityId = String(response.c_task?._id ?? '');
                if (activityId && !seen.has(activityId)) {
                    seen.set(activityId, { label: response.c_task?.c_name ?? activityId, value: activityId });
                }
            }
            return Array.from(seen.values());
        };
        return {
            sites: (sitesQuery.data ?? [])
                .filter((site) => !!site.c_number && site.c_number !== currentSiteNumber)
                .map((site) => ({
                label: [site.c_number, site.c_name].filter(Boolean).join(' — '),
                value: String(site.c_number),
                meta: { siteId: String(site._id) },
            })),
            visits: visitOptions,
            desired_visits: visitOptions.filter((option) => option.value !== selectedVisitId),
            // Site-entered responses are ClinRO; the site response cascade no longer goes through
            // visit, so activities and responses are scoped by activity only
            activities: uniqueActivities(siteResponses),
            visit_activities: uniqueActivities(responsesForVisit),
            site_task_responses: siteResponses
                .filter((response) => !selectedActivityId || String(response.c_task?._id) === selectedActivityId)
                .map((response) => ({
                label: dcrResponseOptionLabel(response, participantTimeZone),
                value: String(response._id),
            })),
            participant_activities: uniqueActivities(participantResponses),
            participant_responses: responses.map((response) => ({
                label: dcrResponseOptionLabel(response, participantTimeZone),
                value: String(response._id),
            })),
            // Participant-entered data only: site-entered responses are corrected through their own type
            epro_responses: participantResponses
                .filter((response) => !selectedActivityId || String(response.c_task?._id) === selectedActivityId)
                .map((response) => ({
                label: dcrResponseOptionLabel(response, participantTimeZone),
                value: String(response._id),
            })),
            /**
             * Screens are picked by the question the participant answered, since the code the activity
             * gives them ("SideInjected") is a build detail the site never sees.
             */
            screens: selectableDcrScreens(screens).map((screen) => ({
                label: screenLabels[String(screen.stepId)] || screen.displayName || screen.name,
                value: String(screen.stepResponseId),
                meta: { ...screen },
            })),
            // The selected screen's choices, so a stored choice code resolves to the label the site
            // picked it by when a request is reviewed
            screen_choices: (selectedScreen?.choices ?? []).map((choice) => ({
                label: String(choice.label ?? choice.c_label ?? choice.value ?? choice.c_value ?? ''),
                value: String(choice.value ?? choice.c_value ?? choice.label ?? ''),
            })),
        };
    }, [
        sitesQuery.data,
        scheduledVisits,
        responses,
        screens,
        screenLabels,
        selectedScreen,
        values.dcr_intake__visit_id,
        values.dcr_intake__activity_id,
        currentSiteNumber,
        participantTimeZone,
    ]);
    return {
        options,
        screens,
        selectedScreen,
        responses,
        scheduledVisits,
        isLoading: sitesQuery.isLoading || visitsQuery.isLoading || responsesQuery.isLoading || flowQuery.isLoading,
    };
};
